import React, {useRef, useCallback, useEffect} from 'react';
import {LocalStyleDesigner} from '@packages/systems/style/engine';
import type {Component} from '@packages/systems/wfdl';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {type BreakpointsMap} from '@packages/systems/style/types';

type Props = {
  componentMap: Component.ComponentMap;
  pageId: string;
  breakpoints: BreakpointsMap<Breakpoint>;
};

const CanvasLocalStyles = ({componentMap, pageId, breakpoints}: Props) => {
  const localStyleRef = useRef<LocalStyleDesigner | undefined>(undefined);

  const styleNodeRef = useCallback(
    (node: HTMLStyleElement) => {
      if (node) {
        localStyleRef.current = new LocalStyleDesigner(
          node,
          Object.keys(breakpoints).map(
            (id) => breakpoints[id as keyof typeof breakpoints]
          )
        );
      }
    },
    [breakpoints]
  );

  // Normally we should use useLayoutEffect for this, but in our case we can't because
  // this component will be rendered to string during the first canvas iframe render.
  // This is also going to be ok here because we don't have a node inside of this component that
  // we need to apply styles to.
  useEffect(() => {
    if (localStyleRef.current) {
      localStyleRef.current.render(componentMap, pageId);
    }
  }, [componentMap, pageId]);

  return <style data-meta="local styles" ref={styleNodeRef} />;
};

export default CanvasLocalStyles;
